import React from 'react'
import { graphql, useFragment } from 'react-relay'
import { LinearProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { formatDurationInSeconds } from 'services/formatting'
import { GraphQLError } from 'types'
import { AnswerListItemStatus_Video$key } from '__generated__/AnswerListItemStatus_Video.graphql'
import { usePolledBackgroundQuery } from 'hooks/usePolledBackgroundQuery'
import { AnswerListItemStatus_Query } from '__generated__/AnswerListItemStatus_Query.graphql'

export interface IAnswerListItemStatusProps {
  videoFragmentRef: AnswerListItemStatus_Video$key
  maxDuration: number
  isUploading: boolean
  isTextAnswered: boolean
  uploadProgress: number
  error: Error | GraphQLError | undefined
}

const PROGRESS_WIDTH = 360

function AnswerListItemStatus(props: IAnswerListItemStatusProps) {
  const { videoFragmentRef, maxDuration, isUploading, isTextAnswered, uploadProgress, error } = props
  const theme = useTheme()

  const videoData = useFragment(
    graphql`
      fragment AnswerListItemStatus_Video on Video {
        id
        status
        rejectReason
      }
    `,
    videoFragmentRef,
  )

  // TODO: this should use a subscription instead of polling, but they are broken currently
  const videoPolledData = usePolledBackgroundQuery<AnswerListItemStatus_Query>(
    graphql`
      query AnswerListItemStatus_Query($id: ID) {
        video(id: $id) {
          status
          rejectReason
          
          contractRequests {
            # This is needed to know if the request has been marked as completed following the processing of the video
            status

            jobApplication {
              # Important to read this for the job screening flow
              status
            }
          }
        }
      }
    `,
    {
      id: videoData?.id,
    },
  )

  const video = videoPolledData?.video ?? videoData

  if (isUploading) {
    return (
      <React.Fragment>
        <LinearProgress
          color="primary"
          variant="determinate"
          value={uploadProgress * 100}
          style={{ maxWidth: PROGRESS_WIDTH, marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
        />
        {Math.round(uploadProgress * 100)}% uploaded
      </React.Fragment>
    )
  }

  if (error) {
    return (
      <span style={{ color: 'rgb(244, 67, 54)' }}>
        Error: {error.message || 'Unknown Error'}
      </span>
    )
  }

  if (
    (video && !video.status) ||
    video?.status === 'processing' ||
    video?.status === 'processing_queued' ||
    video?.status === 'unprocessed' ||
    video?.status === 'pending' ||
    video?.status === 'processing_failed' // This is deliberately included here; the system will retry processing
  ) {
    return (
      <React.Fragment>
        <LinearProgress
          color="primary"
          variant="indeterminate"
          style={{ maxWidth: PROGRESS_WIDTH, marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
        />
        Processing...
      </React.Fragment>
    )
  }

  if (video?.status === 'processing_rejected') {
    return (
      <span style={{ color: 'rgb(244, 67, 54)' }}>
        Processing failed: {video.rejectReason || 'Unknown error. Please try again, or try another browser.'}
      </span>
    )
  }

  if (video?.status === 'processed') {
    return (
      <React.Fragment>
        Processed &amp; Added
      </React.Fragment>
    )
  }

  if (isTextAnswered) {
    return (
      <React.Fragment>
        Added text answer
      </React.Fragment>
    )
  }

  if (maxDuration) {
    return (
      <React.Fragment>
        Max Video Length: {formatDurationInSeconds(maxDuration)}
      </React.Fragment>
    )
  }

  return null
}

export default React.memo(AnswerListItemStatus)
